import { action, computed, makeAutoObservable, observable } from 'mobx';
import { AxiosError, HttpStatusCode } from 'axios';
import { toast } from 'react-toastify';
import { TFunction } from 'i18next';
import { AbortRequestsStore } from '../interfaces';
import ViewStore from '../ViewStore';
import { GetInboxViewerResponse } from './Payloads/responses';
import { DeclarationContext, InboxViewer, TodoListTask } from '90.quickConnect.Models/models';
import { mapBaseInboxItem } from '90.quickConnect.Models/mappings';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { InboxItemType } from '90.quickConnect.Models/enums';
import {
  API_DELETE_INBOXES,
  API_GET_INBOXES,
  API_GET_TODO_LIST_TASKS,
} from '40.quickConnect.DataAccess/axios/apiRoutes';
import buildApiUrl from '40.quickConnect.DataAccess/axios/buildApiUrl';
import RootStore from '30.quickConnect.Stores/RootStore';
import IClientHTTP from '40.quickConnect.DataAccess/ClientHTTP/interface';
import inboxesDb from '40.quickConnect.DataAccess/indexedDb/dbs/inboxesDb';
import { errorHandler } from '80.quickConnect.Core/helpers';

class InboxesStore implements AbortRequestsStore {
  // Tag
  private static readonly TAG = '30.quickConnect.Stores/RootStore/InboxesStores/index.ts';

  clientHTTP: IClientHTTP;

  // Ne pas mettre dans resetStore
  shouldAbortRequests = false;

  numberPreFilledDisplayed = 0;

  PreFilledStore: ViewStore<InboxViewer>;

  TasksStore: ViewStore<InboxViewer>;

  // Nouvelle store pour My own transfers
  MyOwnTransfersStore: ViewStore<InboxViewer>;

  OthersStore: ViewStore<InboxViewer>;

  logger: CustomLogger;

  RootStore: RootStore;

  filter = '';

  finishedTasks: TodoListTask[] = [];

  allTask: TodoListTask[] = [];

  inprogressTask: TodoListTask[] = [];

  // Nombre total d'éléments retourné par l'API
  totalItemsFromAPI = 0;

  // Indique s'il y a plus d'éléments à charger
  isThereMoreItem = false;

  constructor(rootStore: RootStore, logger: CustomLogger) {
    this.logger = logger;
    this.RootStore = rootStore;
    this.clientHTTP = rootStore.clientHTTP;
    makeAutoObservable(
      this,
      {
        finishedTasks: observable,
        allTask: observable,
        inprogressTask: observable,
        totalItemsFromAPI: observable,
        isThereMoreItem: observable,
        inboxesTotalNumber: computed,
        prefilledItemsCount: computed,
        tasksItemsCount: computed,
        othersItemsCount: computed,
        totalItemsCount: computed,
        hasMoreItems: computed,

        setShouldAbortRequests: action,
        getInboxItems: action.bound,
        setInprogressTask: action.bound,
        setFinishedTask: action.bound,
        resetTasks: action.bound,
        setAllTask: action.bound,
        loadMoreItems: action.bound,
        resetItemsToShow: action.bound,
      },
      { autoBind: true },
    );

    this.PreFilledStore = new ViewStore<InboxViewer>(this.logger, 'PreFilledStore');
    this.TasksStore = new ViewStore<InboxViewer>(this.logger, 'TasksStore');
    this.MyOwnTransfersStore = new ViewStore<InboxViewer>(this.logger, 'MyOwnTransfersStore');
    this.OthersStore = new ViewStore<InboxViewer>(this.logger, 'OthersStore');
  }

  resetStore = (): void => {
    this.numberPreFilledDisplayed = 0;
    this.PreFilledStore.reset();
    this.TasksStore.reset();
    this.MyOwnTransfersStore.reset();
    this.OthersStore.reset();
    this.filter = '';
    this.resetItemsToShow();
  };

  resetItemsToShow = (): void => {
    this.totalItemsFromAPI = 0;
    this.isThereMoreItem = false;
  };

  loadMoreItems = async (): Promise<void> => {
    // Prevent loading if already loading or no more items
    if (!this.hasMoreItems) return;

    // Calculer le nombre d'éléments déjà chargés
    const currentlyLoaded =
      this.prefilledItemsCount + this.myOwnTransfersCount + this.tasksItemsCount + this.othersItemsCount;
    // Charger les éléments suivants
    await this.getInboxItems(currentlyLoaded, true);
  };

  setShouldAbortRequests = (shouldAbortRequests: boolean) => (this.shouldAbortRequests = shouldAbortRequests);

  setInprogressTask = (tasks: TodoListTask[]) => {
    this.inprogressTask = tasks;
  };

  setFinishedTask = (tasks: TodoListTask[]) => {
    this.finishedTasks = tasks;
  };

  changeTaskState = (task: TodoListTask, state: boolean) => {
    if (state) {
      this.inprogressTask = this.inprogressTask.filter((x) => x.id !== task.id);
      this.setFinishedTask([...this.finishedTasks, task]);
    } else {
      this.finishedTasks = this.finishedTasks.filter((x) => x.id !== task.id);
      this.inprogressTask = [...this.inprogressTask, task];
    }
  };

  queueInboxDeletionAsync = async (
    context: DeclarationContext,
    userUpn: string,
    t: TFunction,
    inboxId: string | undefined,
    declarationId: string,
    sendAPI?: boolean | undefined,
  ): Promise<boolean> => {
    try {
      if (context) {
        this.logger.resetInitDateTimeApp();
        this.logger.trace(InboxesStore.TAG, "Début de la sauvegarde de la suppression de l'inbox");

        // Si il y a internet et qu'on n'est pas dans une sauvegarde automatique en local, on envoie les inboxes à l'API
        if (sendAPI && inboxId) {
          const inboxToDeleteSaved = await inboxesDb.addInboxToDeleteAsync(userUpn, inboxId, declarationId);

          if (inboxToDeleteSaved) {
            this.logger.trace(InboxesStore.TAG, 'Suppression réussie');
          } else {
            toast.error(
              t('deleteInbox.errorWithMsg', {
                msg: `Statut code`,
              }).toString(),
            );
            return false;
          }
        }
      }
    } catch (error) {
      const isAxios = error instanceof AxiosError;
      errorHandler(InboxesStore.TAG, error, 'queueInboxDeletionAsync', isAxios ? 'warn' : 'error');

      const errorMsg = isAxios
        ? (error as AxiosError<{ message?: string }>).response?.data?.message ?? error.message
        : error instanceof Error
        ? error.message
        : String(error);

      toast.error(t('qcapp_warning_service_send_action_error', { msg: errorMsg }).toString());
    }
    return false;
  };

  async getInboxItems(start = 0, append = false): Promise<void> {
    // Validate input parameters
    if (start < 0 || !Number.isInteger(start)) {
      this.logger.warn(InboxesStore.TAG, `Invalid start parameter: ${start}`);
      return;
    }

    const {
      LoginStore: {
        signInInfos: { userUPN: forThisUPN },
      },
    } = this.RootStore;
    try {
      if (!this.shouldAbortRequests) {
        // Si ce n'est pas un append (chargement initial), on réinitialise tout
        if (!append) {
          this.resetItemsToShow();
        }

        // Mise a jour de uri avec paramètre de pagination
        const getInboxesUri = buildApiUrl(API_GET_INBOXES, { forThisUPN }, { start });

        const response = await this.clientHTTP.get<GetInboxViewerResponse>(
          this.RootStore.CommonStore.chooseBaseUrl(getInboxesUri),
          { withCredentials: true },
        );

        // Récupération du total et du flag depuis la réponse API
        this.totalItemsFromAPI = response.data.totalCount ?? 0;
        this.isThereMoreItem = response.data.isThereMoreItem ?? false;

        let inboxes = (response.data.inboxes ?? []).map(mapBaseInboxItem);
        inboxes = inboxes.sort((a, b) => a.subject.localeCompare(b.subject, undefined, { sensitivity: 'base' }));

        // Séparer tous les inboxes par type
        const preFilled = inboxes.filter((i) => i.inboxType === InboxItemType.PrefilledForm);
        const myOwnTransfers = inboxes.filter((i) => i.inboxType === InboxItemType.TransferredPrefilledForm);
        const tasks = inboxes.filter((i) => i.inboxType === InboxItemType.Task);
        const others = inboxes.filter((i) => i.inboxType === InboxItemType.Unknow);

        // On doit traiter les id qui ont pu etre supprimés.
        const activityIdsAndSentAtDate = await inboxesDb.getActivityIdsAndSentAtDate(forThisUPN);
        if (activityIdsAndSentAtDate !== undefined && activityIdsAndSentAtDate.activityIds.length > 0) {
          const prefilledFiltered = this.reduceInboxDeleted(
            preFilled,
            activityIdsAndSentAtDate.activityIds,
            activityIdsAndSentAtDate.sentAt,
          );
          const myOwnTransfersFiltered = this.reduceInboxDeleted(
            myOwnTransfers,
            activityIdsAndSentAtDate.activityIds,
            activityIdsAndSentAtDate.sentAt,
          );
          const tasksFiltered = this.reduceInboxDeleted(
            tasks,
            activityIdsAndSentAtDate.activityIds,
            activityIdsAndSentAtDate.sentAt,
          );
          const othersFiltered = this.reduceInboxDeleted(
            others,
            activityIdsAndSentAtDate.activityIds,
            activityIdsAndSentAtDate.sentAt,
          );

          // Accumulation ou remplacement selon le paramètre append
          if (append) {
            // Ajouter aux items existants (optimized concatenation)
            this.PreFilledStore.setItems((this.PreFilledStore.items ?? []).concat(prefilledFiltered));
            this.MyOwnTransfersStore.setItems((this.MyOwnTransfersStore.items ?? []).concat(myOwnTransfersFiltered));
            this.TasksStore.setItems((this.TasksStore.items ?? []).concat(tasksFiltered));
            this.OthersStore.setItems((this.OthersStore.items ?? []).concat(othersFiltered));
          } else {
            // Remplacer les items (chargement initial)
            this.PreFilledStore.setItems(prefilledFiltered);
            this.MyOwnTransfersStore.setItems(myOwnTransfersFiltered);
            this.TasksStore.setItems(tasksFiltered);
            this.OthersStore.setItems(othersFiltered);
          }
        } else {
          // Accumulation ou remplacement selon le paramètre append
          if (append) {
            // Ajouter aux items existants (optimized concatenation)
            this.PreFilledStore.setItems((this.PreFilledStore.items ?? []).concat(preFilled));
            this.MyOwnTransfersStore.setItems((this.MyOwnTransfersStore.items ?? []).concat(myOwnTransfers));
            this.TasksStore.setItems((this.TasksStore.items ?? []).concat(tasks));
            this.OthersStore.setItems((this.OthersStore.items ?? []).concat(others));
          } else {
            // Remplacer les items (chargement initial)
            this.PreFilledStore.setItems(preFilled);
            this.MyOwnTransfersStore.setItems(myOwnTransfers);
            this.TasksStore.setItems(tasks);
            this.OthersStore.setItems(others);
          }
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorAxios = error as AxiosError<GetInboxViewerResponse>;
        errorAxios.message = `Une erreur est survenue lors de la récupération des inboxes pour cet utilisateur ${forThisUPN} : Statut: ${errorAxios.response?.data.status} - Message: ${errorAxios.response?.data.message}`;
        errorHandler(InboxesStore.TAG, errorAxios, 'getInboxItems', 'warn');
      } else if (error instanceof Error) {
        error.message = `Une erreur est survenue lors de la récupération des inboxes cet utilisateur ${forThisUPN} : ${error.message}`;
        errorHandler(InboxesStore.TAG, error, 'getInboxItems', 'error');
      }
    }
  }

  async deleteQueuedInboxesAsync(tAxios?: TFunction): Promise<number> {
    const {
      LoginStore: {
        signInInfos: { userUPN },
      },
    } = this.RootStore;

    try {
      if (userUPN) {
        const deleteQueuedInboxesUri = API_DELETE_INBOXES;
        // Charger les inboxIds depuis IndexedDB
        const inboxIdsToDelete = await inboxesDb.getInboxesToDeleteAsync(userUPN);

        if (inboxIdsToDelete.length === 0) {
          this.logger.info(InboxesStore.TAG, `Aucune inbox à supprimer pour l'utilisateur ${userUPN}`);
          return 0;
        }

        // Appel à l'API pour supprimer les inboxIds
        const response = await this.clientHTTP.post(
          this.RootStore.CommonStore.chooseBaseUrl(deleteQueuedInboxesUri),
          { Ids: inboxIdsToDelete },
          {
            withCredentials: true,

            /*
             * Une inbox deja supprimee cote serveur (404) fait partie du protocole: c'est au
             * catch ci-dessous de decider comment le tracer et le traiter, pas au client HTTP
             * generique.
             */
            expectedStatuses: [HttpStatusCode.NotFound],
          },
        );

        if (response.status === 200) {
          const inboxDraftIdsToDelete = await inboxesDb.getInboxDraftIdsByDeclarationIds(inboxIdsToDelete);
          await inboxesDb.inboxDraft.bulkDelete(inboxDraftIdsToDelete);
          this.logger.info(InboxesStore.TAG, `Suppression réussie des inboxes pour l'utilisateur ${userUPN}`);

          // Afficher un toast si tAxios est fourni (lors de la synchro automatique)
          if (tAxios && inboxIdsToDelete.length > 0) {
            toast.success(tAxios('qcapp_sync_message_inboxes', { count: inboxIdsToDelete.length }));
          }

          return inboxIdsToDelete.length;
        } else {
          this.logger.warn(
            InboxesStore.TAG,
            `Échec de la suppression des inboxes pour l'utilisateur ${userUPN}. Statut: ${response.status}`,
          );

          // Afficher un toast d'erreur si tAxios est fourni
          if (tAxios) {
            toast.error(tAxios('qcapp_delete_inbox_fail'));
          }

          return 0;
        }
      }
      return 0;
    } catch (error) {
      if (error instanceof AxiosError) {
        // Gestion spécifique de l'erreur "inbox non trouvée" (déjà supprimée)
        const errorCode = error.response?.data?.errorCode;
        const isInboxNotFound = errorCode === 'MSG_SQL_DELETE_INBOX_NOT_FOUND' || error.response?.data?.Code === 50001;

        if (isInboxNotFound) {
          // L'inbox n'existe plus côté serveur, on nettoie quand même IndexedDB
          this.logger.info(
            InboxesStore.TAG,
            `Inbox(es) déjà supprimée(s) côté serveur (${errorCode}). Nettoyage de la queue locale.`,
          );

          const inboxIdsToDelete = await inboxesDb.getInboxesToDeleteAsync(userUPN);
          if (inboxIdsToDelete.length > 0) {
            const inboxDraftIdsToDelete = await inboxesDb.getInboxDraftIdsByDeclarationIds(inboxIdsToDelete);
            await inboxesDb.inboxDraft.bulkDelete(inboxDraftIdsToDelete);
          }

          // Suppression silencieuse
          return 0;
        }

        errorHandler(InboxesStore.TAG, error, 'deleteQueuedInboxesAsync', 'warn');
      } else {
        this.logger.error(InboxesStore.TAG, `Erreur inattendue: ${error}`);
      }

      // Afficher un toast d'erreur si tAxios est fourni
      if (tAxios) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        toast.error(tAxios('qcapp_error_delete_inbox_operation', { msg: errorMsg }));
      }

      return 0;
    }
  }

  /** Inbox chargée dans les listes, undefined si elle n'y figure pas */
  private findLoadedInbox = (inboxId: string): InboxViewer | undefined =>
    [this.PreFilledStore, this.TasksStore, this.MyOwnTransfersStore, this.OthersStore]
      .flatMap((store) => store.items ?? [])
      .find((inbox) => inbox.id === inboxId);

  /** UO d'une inbox chargée dans les listes, undefined si elle n'y figure pas */
  getInboxOuCode = (inboxId: string): string | undefined => this.findLoadedInbox(inboxId)?.ouCode;

  /** Sujet d'une inbox chargée dans les listes, undefined si elle n'y figure pas */
  getInboxSubject = (inboxId: string): string | undefined => this.findLoadedInbox(inboxId)?.subject;

  /**
   * Supprime une inbox de la boîte de réception sans l'ouvrir (ex: inbox d'une UO hors du périmètre de l'utilisateur) :
   * elle disparaît tout de suite de la liste et sa suppression est envoyée au serveur, ou à la prochaine synchronisation
   */
  deleteInboxAsync = async (inbox: InboxViewer): Promise<void> => {
    const { userUPN } = this.RootStore.LoginStore.signInInfos;
    // Inbox jamais ouverte : pas de déclaration, l'identifiant de l'inbox en tient lieu dans la file de suppression
    if (!(await inboxesDb.addInboxToDeleteAsync(userUPN, inbox.id, inbox.id))) return;

    await this.removeInboxActivityId(userUPN, inbox.activityId, inbox.sentAt);
    await this.deleteQueuedInboxesAsync();
  };

  reduceInboxDeleted = (items: InboxViewer[], activityIdsToFilter: string[], originalSentAt: Date): InboxViewer[] =>
    items.filter((item) => (activityIdsToFilter.includes(item.activityId) ? item.sentAt > originalSentAt : true));

  async removeInboxActivityId(userUPN: string, activityId?: string, sentAt?: Date): Promise<boolean> {
    if (!activityId) return false;

    const bInsertActivityId = await inboxesDb.addOrUpdateInboxAsync(activityId, userUPN, sentAt);

    if (!bInsertActivityId) return false;

    this.PreFilledStore.removeActivityId(activityId);
    this.MyOwnTransfersStore.removeActivityId(activityId);
    this.TasksStore.removeActivityId(activityId);
    this.OthersStore.removeActivityId(activityId);

    return true;
  }

  get inboxesTotalNumber() {
    return this.totalItemsFromAPI;
  }

  get prefilledItemsCount() {
    return this.PreFilledStore.items?.length ?? 0;
  }

  get myOwnTransfersCount() {
    return this.MyOwnTransfersStore.items?.length ?? 0;
  }

  get tasksItemsCount() {
    return this.TasksStore.items?.length ?? 0;
  }

  get othersItemsCount() {
    return this.OthersStore.items?.length ?? 0;
  }

  // ============= Chargement progressif (Load More) =============
  // Computed property : nombre total d'éléments retourné par l'API
  get totalItemsCount(): number {
    return this.totalItemsFromAPI;
  }

  // Computed property : indique s'il reste des éléments à charger
  get hasMoreItems(): boolean {
    return this.isThereMoreItem;
  }

  // Computed properties : items affichés regroupés par catégorie
  // Ces propriétés retournent directement les items des stores (déjà filtrés par l'API)
  get displayedPreFilledItems(): InboxViewer[] {
    return this.PreFilledStore.items ?? [];
  }

  get displayedMyOwnTransfersItems(): InboxViewer[] {
    return this.MyOwnTransfersStore.items ?? [];
  }

  get displayedTasksItems(): InboxViewer[] {
    return this.TasksStore.items ?? [];
  }

  get displayedOthersItems(): InboxViewer[] {
    return this.OthersStore.items ?? [];
  }
  // ============= Fin chargement progressif =============

  filterThisInboxes = (type: InboxItemType) => {
    switch (type) {
      case InboxItemType.PrefilledForm:
        return this.PreFilledStore.items
          ? this.PreFilledStore.filterThis(this.PreFilledStore.items, (i: InboxViewer) => i.subject)
          : [];
      case InboxItemType.TransferredPrefilledForm:
        return this.MyOwnTransfersStore.items
          ? this.MyOwnTransfersStore.filterThis(this.MyOwnTransfersStore.items, (i: InboxViewer) => i.subject)
          : [];
      case InboxItemType.Task:
        return this.TasksStore.items
          ? this.TasksStore.filterThis(this.TasksStore.items, (i: InboxViewer) => i.subject)
          : [];
      default:
      case InboxItemType.Unknow:
        return this.OthersStore.items
          ? this.OthersStore.filterThis(this.OthersStore.items, (i: InboxViewer) => i.subject)
          : [];
    }
  };

  setAllTask = (data: TodoListTask[]) => {
    this.allTask = data;
  };

  resetTasks = () => {
    this.setInprogressTask([]);
    this.setFinishedTask([]);
    this.setAllTask([]);
  };

  async getTodoListTask(activityId: string) {
    try {
      const result = await this.clientHTTP.post(
        this.RootStore.CommonStore.chooseBaseUrl(API_GET_TODO_LIST_TASKS),
        { id: activityId },
        {
          withCredentials: true,
        },
      );
      if (result) {
        const { data } = result;
        this.setAllTask(data.todoListTasks);

        this.setInprogressTask(data.todoListTasks.filter((x: TodoListTask) => x.state == 0));
        const inprogressTaskData = this.inprogressTask.map(
          (x: TodoListTask) => x && { ...x, comment: '', attachment: null },
        );
        this.setInprogressTask(inprogressTaskData);
        this.setFinishedTask(data.todoListTasks.filter((x: TodoListTask) => x.state == 100));
        this.setFinishedTask(this.finishedTasks.map((x: TodoListTask) => x && { ...x, comment: '', attachment: null }));

        return data;
      }
    } catch (error) {
      errorHandler(InboxesStore.TAG, error, 'getTodoListTask');
    }
  }
}
export default InboxesStore;
